import { useState, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { useCreateRole, useUpdateRole, useManageableCompanies } from '../api';
import { isPlanLimitError } from '@/lib/permissions/quotaError';
import type { AccessRole } from '@/lib/api/accessManager';
import { useAuthStore } from '@/lib/auth/authStore';
import { useT } from '@/lib/i18n/useT';
import { PermissionDualList, type PermissionItem } from './PermissionDualList';
import { type Action, entityMatchesApp, permissionId } from '../permissionCatalog';
import { presetsForApp, type PermissionPreset } from '../permissionPresets';
import { Sparkles, Eraser } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: AccessRole | null;
  /**
   * When provided AND `editing` is null, the dialog opens in CREATE mode with
   * name + permissions pre-filled from the source role. Company scope is left
   * empty so the manager must pick a target. Used by the "Clone" action.
   */
  cloneFrom?: AccessRole | null;
  /** Fired when backend rejects with PLAN_LIMIT_EXCEEDED — callers open
   * UpgradeDialog instead of showing a raw error toast. */
  onQuotaExceeded?: () => void;
}

export function RoleFormDialog({ open, onOpenChange, editing, cloneFrom, onQuotaExceeded }: Props) {
  const t = useT();
  const create = useCreateRole();
  const update = useUpdateRole();
  const companies = useManageableCompanies();
  const sessionApp = useAuthStore((s) => s.app);
  const ownCompanyId = useAuthStore((s) => s.user?.companyId) ?? null;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // App is implicitly the current session app — not user-selectable.
  const type = sessionApp ?? 'xotski';
  const [companyIds, setCompanyIds] = useState<Set<number>>(new Set());
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  /**
   * Permissions on entities that belong to OTHER apps. The dialog hides them
   * from the editor (scoped to the current app) but merges them back on submit
   * so the manager doesn't accidentally wipe sibling-app permissions.
   */
  const [preserved, setPreserved] = useState<PermissionItem[]>([]);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setDescription(editing.description ?? '');
      // Hydrate scope from junction (preferred) or legacy companyId.
      const initialScopes = new Set<number>();
      if (editing.scopes && editing.scopes.length > 0) {
        for (const s of editing.scopes) initialScopes.add(s.scopedCompanyId);
      } else if (editing.companyId) {
        initialScopes.add(editing.companyId);
      }
      setCompanyIds(initialScopes);
      const all = (editing.permissions ?? []).map((p) => ({
        entity: p.entity,
        action: p.action as Action,
      }));
      const inScope: PermissionItem[] = [];
      const outOfScope: PermissionItem[] = [];
      for (const p of all) {
        if (entityMatchesApp(p.entity, sessionApp)) inScope.push(p);
        else outOfScope.push(p);
      }
      setPermissions(inScope);
      setPreserved(outOfScope);
    } else if (cloneFrom) {
      // Clone: pre-fill name + permissions, force user to pick companies.
      setName(`${cloneFrom.name} (copy)`);
      setDescription(cloneFrom.description ?? '');
      setCompanyIds(new Set());
      const all = (cloneFrom.permissions ?? []).map((p) => ({
        entity: p.entity,
        action: p.action as Action,
      }));
      setPermissions(all.filter((p) => entityMatchesApp(p.entity, sessionApp)));
      setPreserved([]);
    } else {
      setName('');
      setDescription('');
      // Default: bind to manager's own company (full tree of descendants).
      setCompanyIds(ownCompanyId ? new Set([ownCompanyId]) : new Set());
      setPermissions([]);
      setPreserved([]);
    }
  }, [editing, cloneFrom, sessionApp, ownCompanyId, open]);

  const isEditing = !!editing;
  const isSaving = create.isPending || update.isPending;

  function submit() {
    if (!name.trim()) {
      toast({ tone: 'error', title: t('roleForm.nameRequired') });
      return;
    }
    if (companyIds.size === 0) {
      toast({ tone: 'error', title: t('roleForm.companyRequired') });
      return;
    }
    const idsArr = [...companyIds];
    const mergedPermissions = [
      ...permissions.map((p) => ({ entity: p.entity, action: p.action })),
      ...preserved.map((p) => ({ entity: p.entity, action: p.action })),
    ];

    if (isEditing) {
      update.mutate(
        {
          id: editing!.id,
          body: {
            name: name.trim(),
            description: description.trim(),
            companyIds: idsArr,
            permissions: mergedPermissions,
          },
        },
        {
          onSuccess: () => {
            toast({ tone: 'success', title: t('roleForm.updated') });
            onOpenChange(false);
          },
          onError: (e: Error) => toast({ tone: 'error', title: e.message }),
        },
      );
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        description: description.trim(),
        type,
        companyIds: idsArr,
        permissions: mergedPermissions,
      },
      {
        onSuccess: () => {
          toast({ tone: 'success', title: t('roleForm.created') });
          onOpenChange(false);
        },
        onError: (e: Error) => {
          if (isPlanLimitError(e) && onQuotaExceeded) {
            onOpenChange(false);
            onQuotaExceeded();
            return;
          }
          toast({ tone: 'error', title: e.message });
        },
      },
    );
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[var(--color-bg-overlay)] backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-5xl -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto scrollbar-thin rounded-2xl bg-[var(--color-bg-surface)] p-6 text-[var(--color-fg-primary)] shadow-[var(--shadow-elevated)] border border-[var(--color-border-default)]',
          )}
        >
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? t('roleForm.titleEdit', { name: editing!.name })
                : cloneFrom
                  ? t('roleForm.titleClone', { name: cloneFrom.name })
                  : t('roleForm.titleCreate')}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? t('roleForm.subtitleEdit')
                : cloneFrom
                  ? t('roleForm.subtitleClone', { name: cloneFrom.name })
                  : t('roleForm.subtitleCreate')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-3">
                <label className="block space-y-1.5">
                  <span className="text-xs font-semibold text-[var(--color-fg-muted)]">
                    {t('roleForm.name')}
                  </span>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('roleForm.namePlaceholder')}
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs font-semibold text-[var(--color-fg-muted)]">
                    {t('roleForm.description')}{' '}
                    <span className="text-[var(--color-fg-subtle)]">
                      {t('roleForm.optional')}
                    </span>
                  </span>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('roleForm.descriptionPlaceholder')}
                    maxLength={500}
                    rows={2}
                    className="w-full resize-y rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-3 py-2 text-sm text-[var(--color-fg-primary)] placeholder:text-[var(--color-fg-subtle)] focus:border-[var(--color-accent-ink)] focus:outline-none"
                  />
                  <span className="text-[10px] text-[var(--color-fg-subtle)]">
                    {t('roleForm.descriptionHint')}
                  </span>
                </label>
              </div>
              <div className="space-y-1.5">
                <span className="text-xs font-semibold text-[var(--color-fg-muted)]">
                  {t('roleForm.brandScope', { n: String(companyIds.size) })}
                </span>
                <div className="max-h-[140px] overflow-y-auto rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] p-2 scrollbar-thin">
                  {(companies.data?.data?.items ?? []).length === 0 && (
                    <p className="px-1 py-2 text-xs text-[var(--color-fg-muted)]">
                      {t('roleForm.noBrands')}
                    </p>
                  )}
                  {(companies.data?.data?.items ?? []).map((c) => {
                    const isOwn = c.id === ownCompanyId;
                    const checked = companyIds.has(c.id);
                    return (
                      <label
                        key={c.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-[var(--color-bg-elevated)]"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = new Set(companyIds);
                            if (checked) next.delete(c.id);
                            else next.add(c.id);
                            setCompanyIds(next);
                          }}
                          className="h-3.5 w-3.5 accent-[var(--color-accent)]"
                        />
                        <span className="flex-1">{c.name}</span>
                        {isOwn && (
                          <span className="text-[10px] text-[var(--color-fg-subtle)]">
                            full tree
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
                <span className="text-[10px] text-[var(--color-fg-subtle)]">
                  {t('roleForm.brandScopeHint')}
                </span>
              </div>
            </div>

            <PermissionPresetBar
              app={sessionApp}
              current={permissions}
              onApply={(preset) => {
                const have = new Set(permissions.map((p) => permissionId(p.entity, p.action)));
                const next = [...permissions];
                for (const p of preset.permissions) {
                  if (!have.has(permissionId(p.entity, p.action))) next.push(p);
                }
                setPermissions(next);
                toast({ tone: 'success', title: `Applied · ${preset.label}` });
              }}
              onClear={() => setPermissions([])}
            />

            {/* Task 6.1 / 6.2 — presets are the screen; the raw capability
                list is the escape hatch. It used to render expanded by default,
                so choosing a role meant reading 119 checkboxes before finding
                out that "Events editor" was one click away. */}
            <details className="rounded-lg border border-[var(--color-border-subtle)]">
              <summary className="cursor-pointer select-none px-3 py-2 text-xs font-bold text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]">
                {t('access.roles.advanced')}
              </summary>
              <div className="border-t border-[var(--color-border-subtle)] p-3">
                <p className="mb-3 text-[11px] text-[var(--color-fg-subtle)]">
                  {t('access.roles.advancedHint')}
                </p>
                <PermissionDualList value={permissions} onChange={setPermissions} app={sessionApp} />
              </div>
            </details>
            {preserved.length > 0 && (
              <p className="rounded-md bg-[var(--color-bg-surface)] p-2 text-[11px] text-[var(--color-fg-muted)]">
                <strong className="text-[var(--color-warn)]">{preserved.length}</strong>{' '}
                permission(s) on entities owned by other apps are preserved as-is — switch to that
                app to manage them.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} loading={isSaving}>
              {isEditing
                ? t('roleForm.save')
                : cloneFrom
                  ? t('roleForm.createClone')
                  : t('roleForm.titleCreate')}
            </Button>
          </DialogFooter>

          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-md p-1 text-[var(--color-fg-muted)] hover:bg-[var(--color-bg-surface)] hover:text-[var(--color-fg-primary)] focus:outline-none">
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function PermissionPresetBar({
  app,
  current,
  onApply,
  onClear,
}: {
  app: string | null | undefined;
  current: PermissionItem[];
  onApply: (preset: PermissionPreset) => void;
  onClear: () => void;
}) {
  const t = useT();
  const presets = presetsForApp(app);
  if (presets.length === 0) return null;
  const currentIds = new Set(current.map((p) => permissionId(p.entity, p.action)));
  return (
    <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-[var(--color-accent-ink)]" />
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--color-fg-subtle)]">
            {t('roleForm.presets')}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          disabled={current.length === 0}
          title={t('roleForm.clearHint')}
        >
          <Eraser className="mr-1 h-3.5 w-3.5" />
          {t('roleForm.clear')}
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {presets.map((preset) => {
          const presetIds = preset.permissions.map((p) => permissionId(p.entity, p.action));
          const allApplied =
            presetIds.length > 0 && presetIds.every((id) => currentIds.has(id));
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => onApply(preset)}
              title={preset.description}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors',
                allApplied
                  ? 'border-[var(--color-accent-ink)] bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]'
                  : 'border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] text-[var(--color-fg-primary)] hover:border-[var(--color-accent-ink)] hover:text-[var(--color-accent-ink)]',
              )}
            >
              + {preset.label}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[10px] text-[var(--color-fg-subtle)]">
        {t('roleForm.presetsHint')}
      </p>
    </div>
  );
}
